import { ReactElement, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { NewProject, SortDirection, SortBy, Division, UserDetails } from 'types/domainTypes';
import { useSaveProjectMutation, useGetProjectsQuery } from '../../redux/services/projects';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { updateDivision, updateName, updateEmail } from '../../redux/slices/userSlice';
import { setPage, setSortBy, setSortDirection, setSearch } from '../../redux/slices/projectsPageSlice';
import AppBodyContainer from 'components/AppBodyContainer/AppBodyContainer';
import CenteredLoadingDisplay from 'components/CenteredLoadingDisplay/CenteredLoadingDisplay';
import { AppContainer } from '../../components/AppContainer/AppContainer';
import { SelectSortDropdown } from '../../components/SelectSortDropdown/SelectSortDropdown';
import { ShrinkToIconButton } from '../../components/ShrinkToIconButton/ShrinkToIconButton';
import { PaginationWithTotal } from 'components/PaginationWithTotal/PaginationWithTotal';
import { ProjectPageHeader } from './ProjectPageHeader';
import { NewProjectDialog } from './NewProjectDialog';
import { ProjectListDisplay } from './ProjectListDisplay';
import { useGetSupportWidthsQuery } from 'redux/services/supportWidths';

interface ProjectPageProps {
  division: Division;
  user: UserDetails;
}

function ProjectPage({ division, user }: ProjectPageProps): ReactElement {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { page, sortBy, sortDirection, search } = useAppSelector((state) => state.projectsPage);
  const dispatch = useAppDispatch();
  const [searchInput, setSearchInput] = useState(search);

  const {
    data: projects,
    error: getProjectsError,
    isLoading: isProjectsLoading,
  } = useGetProjectsQuery({ pageSize: 24, pageNumber: page, sortBy, sortDirection, division, search }, { refetchOnMountOrArgChange: true });
  const [createProject, { data: createdProject, error: createProjectError, isLoading: isCreateProjectLoading }] = useSaveProjectMutation();
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false);

  const { error: getSupportWidthsError, isLoading: isSupportWidthLoading } = useGetSupportWidthsQuery(null);

  useEffect(() => {
    dispatch(updateDivision(division));
  }, [dispatch, division]);

  useEffect(() => {
    dispatch(updateName(user.name));
    dispatch(updateEmail(user.email));
  }, [dispatch, user.name, user.email]);

  useEffect(() => {
    const notficationKey = 'support-widths-request-error-notificaton';
    if (getSupportWidthsError) {
      enqueueSnackbar(t('projects-page.error-message-support-widths'), {
        variant: 'error',
        key: notficationKey,
      });
    } else {
      closeSnackbar(notficationKey);
    }
  }, [enqueueSnackbar, closeSnackbar, getSupportWidthsError, t]);

  useEffect(() => {
    const notficationKey = 'projects-search-request-error-notificaton';
    if (getProjectsError) {
      enqueueSnackbar(t('projects-page.error-message-loading-projects'), {
        variant: 'error',
        key: notficationKey,
      });
    } else {
      closeSnackbar(notficationKey);
    }
  }, [enqueueSnackbar, closeSnackbar, getProjectsError, t]);

  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      if (searchInput !== search) dispatch(setSearch(searchInput));
    }, 1000);

    return () => clearTimeout(searchTimeout);
  }, [searchInput, dispatch, search]);

  function onMovePage(pageNumber: number) {
    dispatch(setPage(pageNumber));
  }

  function handleSetSortBy(value: SortBy) {
    dispatch(setSortBy(value));
  }

  function handleSetSortDirection() {
    if (sortDirection === SortDirection.Ascending) {
      dispatch(setSortDirection(SortDirection.Descending));
    } else {
      dispatch(setSortDirection(SortDirection.Ascending));
    }
  }

  function handleSetSearch(value: string) {
    setSearchInput(value);
  }

  const handleOpenNewProjectDialog = () => {
    setIsNewProjectDialogOpen(true);
  };

  const handleCloseNewProjectDialog = () => {
    setIsNewProjectDialogOpen(false);
  };

  const handleCreateProject = async (project: NewProject) => {
    await createProject(project);
  };

  function onNewProjectClick() {
    handleOpenNewProjectDialog();
  }

  if (createdProject != null) {
    return <Navigate to={`/project/${createdProject.id}`} />;
  }

  const isLoading = isProjectsLoading || isCreateProjectLoading || isSupportWidthLoading;
  const sortDropdownOptions = Object.keys(SortBy);
  const title = t('projects-page.project-dialog.new-project-header');

  return (
    <AppContainer header={<ProjectPageHeader handleSetSearch={handleSetSearch} searchInput={searchInput} />}>
      <AppBodyContainer data-testid="project-page-container">
        {projects && (
          <Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" marginBottom="24px">
              <Typography variant="h1">{t('projects-page.label-title')}</Typography>
              <ShrinkToIconButton
                data-testid="headerNewProject"
                buttonText={t('projects-page.new-project-button')}
                onClick={onNewProjectClick}
                variant="contained"
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" marginBottom="24px">
              <SelectSortDropdown
                options={sortDropdownOptions}
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSetSortBy={handleSetSortBy}
                handleSetSortDirection={handleSetSortDirection}
              />
              <PaginationWithTotal
                totalNumber={projects.total}
                totalPagesNumber={projects.totalPages}
                onMovePage={onMovePage}
                page={page}
              />
            </Stack>
          </Stack>
        )}
        <ProjectListDisplay onNewProjectClick={onNewProjectClick} response={projects} search={search} />
        {projects && (
          <Stack>
            <Stack direction="row" justifyContent="end" alignItems="center" width="100%" marginBottom="24px">
              <PaginationWithTotal
                totalNumber={projects.total}
                totalPagesNumber={projects.totalPages}
                onMovePage={onMovePage}
                page={page}
              />
            </Stack>
          </Stack>
        )}
        {isNewProjectDialogOpen && (
          <NewProjectDialog
            title={title}
            onClose={handleCloseNewProjectDialog}
            onSaveProject={handleCreateProject}
            error={createProjectError}
          />
        )}
        {isLoading && <CenteredLoadingDisplay />}
      </AppBodyContainer>
    </AppContainer>
  );
}

export default ProjectPage;
